/**
 * Centralised React Query configuration.
 *
 * All query keys and per-query options live here so that any page or
 * component can import the correct settings without duplication.
 * To add a new resource: define its key and options in the relevant
 * section and import them where needed.
 */

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const queryKeys = {
  cameraGroups: {
    all: () => ['camera-groups', 'all'] as const,
    settingsStatus: (id: number) => ['camera-groups', id, 'settings-status'] as const,
    defaultViewPreview: (id: number, value: string) => ['camera-groups', id, 'default-view-preview', value] as const,
    detectionModels: (id: number) => ['camera-groups', id, 'detection-models'] as const,
    detectionModelPreview: (id: number, modelId: number, subscribed: boolean) => ['camera-groups', id, 'detection-model-preview', modelId, subscribed] as const,
    audioClasses: (id: number) => ['camera-groups', id, 'audio-classes'] as const,
    audioClassPreview: (id: number, classId: number, enabled: boolean, threshold: number | null | undefined) => ['camera-groups', id, 'audio-class-preview', classId, enabled, threshold === undefined ? 'keep' : threshold === null ? 'default' : threshold] as const,
    critical: (id: number) => ['camera-groups', id, 'critical'] as const, // #294
    criticalPreview: (id: number) => ['camera-groups', id, 'critical-preview'] as const, // #294 — the dry run of switching on
    levelPreview: (id: number) => ['camera-groups', id, 'level-preview'] as const, // #406 — the 30-day replay of candidate rules
    // CP 391 — "My phone alerts for this group": how many cameras are on each mode now, and the dry run of a choice
    // (categories joined in a fixed order). Both live only while the dialog is open (staleTime 0, gcTime 0).
    myPush: (id: number) => ['camera-groups', id, 'my-push'] as const,
    myPushPreview: (id: number, mode: string, categories: string, suspicious?: boolean) => ['camera-groups', id, 'my-push-preview', mode, categories, suspicious] as const,
  },
  cameras: {
    all: () => ['cameras', 'all'] as const,
    paginated: (search?: string) => ['cameras', 'paginated', search ?? ''] as const,
    infinite: (
      search: string,
      group: string,
      ingestionType: string,
      onlineStatus: string,
      batteryLow: string,
      ordering: string,  // #277
    ) => ['cameras', 'infinite', search, group, ingestionType, onlineStatus, batteryLow, ordering] as const,
    summary: () => ['cameras', 'summary'] as const,
    mapPins: () => ['cameras', 'map-pins'] as const,
    stats: () => ['cameras', 'stats'] as const,
    // #323 — the super admin's push list (every camera, scope=all); under 'cameras' so a push toggle's invalidateQueries(['cameras']) refreshes it
    pushSettingsAdmin: () => ['cameras', 'push-settings-admin'] as const,
    // CP 391 — the requester's own phone alerts for one camera; under 'cameras' so a change of the camera-wide push
    // switch or filter (invalidateQueries(['cameras'])) refreshes its limits line too
    myPush: (id: number) => ['cameras', 'my-push', id] as const,
    // #406 — the camera's latest picture, under the zone editor ("At the train" zone); under 'cameras', never
    // 'events', so the events caches' optimistic patches and refreshes leave it alone
    levelZonePicture: (id: number) => ['cameras', 'level-zone-picture', id] as const,
  },
  pushPreferences: {
    // CP 391 — the person's own camera rows with their stored filters (the bell, PushDeadZones). Its own root, beside
    // 'push-preferences-catalog', so it never prefix-matches the account preferences key ['push-preferences'].
    cameras: () => ['push-preferences-cameras'] as const,
  },
  events: {
    recent: () => ['events', 'recent'] as const,
    paginated: (filters: object) => ['events', 'paginated', filters] as const,
    favourites: (filters: object) => ['events', 'favourites', filters] as const,
    unreadCount: () => ['events', 'unread-count'] as const, // #292
    total: () => ['events', 'total'] as const, // #304 — Total Events: every event, motion included
    criticalOpen: () => ['events', 'critical-open'] as const, // #294 — open critical events (pinned rows, alert bar, popups, tab title, sound)
    // #406 — the dashboard's Suspicious block: unseen suspicious events of the last 24 h. Its OWN key, never
    // criticalOpen (that one feeds the critical sound controller); under 'events' so Seen's optimistic patch reaches it
    suspiciousOpen: () => ['events', 'suspicious-open'] as const,
  },
  // 409 — pose review (superusers): one event's stored pose, and the review counts of a clue
  eventPose: (id: number) => ['event-pose', id] as const,
  reviewSummary: (clue: string) => ['review-summary', clue] as const,
  detectionClasses: () => ['detection-classes'] as const,
  aiModels: () => ['ai-models'] as const,
  globalThresholds: () => ['global-thresholds'] as const,
  notifications: {
    unread: () => ['notifications', 'unread'] as const,
  },
  cameraExchangeLogs: (params: object) => ['camera-exchange-logs', params] as const,
  g100Logs: (params: object) => ['g100-logs', params] as const,
  // #311 — G100 live view
  g100Live: {
    status: (cameraId: number) => ['g100-live', cameraId, 'status'] as const,
  },
  // #309 — G100 Test Console
  // #403 — the owner API's view of a G100 camera (the customer settings sheet)
  g100Owner: {
    status: (cameraId: number) => ['g100-owner', cameraId, 'status'] as const,
    // #411 — the camera's action history (the Status and actions sheet)
    actions: (cameraId: number) => ['g100-owner', cameraId, 'actions'] as const,
  },
  g100Console: {
    cameras: () => ['g100-console', 'cameras'] as const,
    // #399 — stored firmware files (one list for every camera)
    firmware: () => ['g100-console', 'firmware'] as const,
    info: (cameraId: number) => ['g100-console', cameraId, 'info'] as const,
    commands: (cameraId: number) => ['g100-console', cameraId, 'commands'] as const,
    messagesAll: (cameraId: number) => ['g100-console', cameraId, 'messages'] as const,
    messages: (cameraId: number, page: number) => ['g100-console', cameraId, 'messages', page] as const,
    // #310 — the owner's audio library and the camera's own audio list
    audio: (cameraId: number) => ['g100-console', cameraId, 'audio'] as const,
    // #379 — the settings the server holds for the camera
    settings: (cameraId: number) => ['g100-console', cameraId, 'settings'] as const,
    // #381 — the live status the Commands card's liveAction form reads (defaults, open session)
    liveStatus: (cameraId: number) => ['g100-console', cameraId, 'live-status'] as const,
  },
  mapConfig: {
    active: (platform: 'web' | 'ios' | 'android' = 'web') => ['mapConfig', platform] as const,
  },
  systemSettings: {
    // #248 — the tab the Events page opens on, one value for every user.
    defaultEventsView: () => ['system-settings', 'default-events-view'] as const,
    // #294 redesign — the super admin's critical sound rules (sound, repeat, stop after).
    criticalSound: () => ['system-settings', 'critical-sound'] as const,
  },
  // #363 — the site drawings: one root, so a change to a drawing refreshes the list and every drawing at once.
  sitePlans: {
    all: () => ['site-plans'] as const,
    list: () => ['site-plans', 'list'] as const,
    group: (groupId: number) => ['site-plans', 'group', groupId] as const,
  },
}

// ---------------------------------------------------------------------------
// Polling intervals (ms)
// ---------------------------------------------------------------------------

const POLL_DASHBOARD_CAMERAS = 60_000
const POLL_DASHBOARD_EVENTS  = 30_000
const POLL_MAP_PINS          = 60_000   // #336

// #336 — the live queries below refetch when the tab comes back to the front (only when older than their stale
// time). main.tsx keeps the global default refetchOnWindowFocus: false, so settings, contacts and push preferences
// are unchanged.

// ---------------------------------------------------------------------------
// Per-resource query options
// ---------------------------------------------------------------------------

export const queryOptions = {
  cameraGroups: {
    all: {
      staleTime: 60_000,
    },
    // Cached between the dialog's steps; an apply invalidates it.
    settingsStatus: {
      staleTime: 30_000,
    },
  },
  cameras: {
    all: {
      staleTime: 30_000,
      refetchInterval: POLL_DASHBOARD_CAMERAS,
      refetchOnWindowFocus: true,  // #336
    },
    paginated: {
      staleTime: 30_000,
    },
    // #336 — the fleet map's pins (DashboardPage and PersistentDashboardMap share the one query): polled every minute
    // while the tab is visible, and on focus. The persistent map (every page) turns both off for its own observer, so
    // only the Dashboard polls. The WebSocket refreshes them on a live event (#247) and a reconnect.
    mapPins: {
      staleTime: 60_000,
      refetchInterval: POLL_MAP_PINS,
      refetchOnWindowFocus: true,
    },
    infinite: {
      staleTime: 30_000,
      // An infinite-query refetch re-fetches EVERY cached page, sequentially
      // (TanStack Query v5). Polling a 5-page list therefore fires 5 serial
      // requests a minute, forever. Poll only while the user is at page 1.
      // NOTE: `maxPages` is deliberately NOT used — it evicts pages from the
      // opposite end as you fetch, shifting the list and invalidating a
      // restored scroll offset.
      refetchInterval: (query: { state: { data?: { pages: unknown[] } } }) =>
        (query.state.data?.pages.length ?? 1) > 1 ? false : POLL_DASHBOARD_CAMERAS,
    },
  },
  events: {
    recent: {
      staleTime: 30_000,
      refetchInterval: POLL_DASHBOARD_EVENTS,
      // #328 — the WebSocket provider keeps this query alive on every page as a second announcer of new events
      // (the live socket may be deaf); a hidden tab keeps polling, so its alerts still come
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: true,  // #336
    },
    paginated: {
      staleTime: 30_000,
    },
    // #292 — refreshed with the recent events: on every live event (useWebSocket) and every 30 s.
    unreadCount: {
      staleTime: 30_000,
      refetchInterval: POLL_DASHBOARD_EVENTS,
      refetchOnWindowFocus: true,  // #336
    },
    // #304 — the Total Events card's count, refreshed like the recent list (WS + 30 s).
    total: {
      staleTime: 30_000,
      refetchInterval: POLL_DASHBOARD_EVENTS,
      refetchOnWindowFocus: true,  // #336
    },
    // #294 — the open critical events, on every page (alert bar, popups, tab title, sound): refreshed by the
    // WebSocket (a critical event, an acknowledgement) and every 30 s as a safety net.
    criticalOpen: {
      staleTime: 30_000,
      refetchInterval: POLL_DASHBOARD_EVENTS,
      // a hidden tab keeps polling: its alert bar, tab title and sound must not miss a critical event
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: true,  // #336
    },
    // #406 — the Suspicious block (the Dashboard page only): the WebSocket seeds it on a suspicious event and prunes
    // it on a Seen; polled every 30 s and on focus as a safety net. Nothing sounds from it, so a hidden tab need not poll.
    suspiciousOpen: {
      staleTime: 30_000,
      refetchInterval: POLL_DASHBOARD_EVENTS,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: true,
    },
  },
  detectionClasses: {
    staleTime: Infinity,
  },
  aiModels: {
    staleTime: Infinity,
  },
  systemSettings: {
    // #248 — fetched once per session; a super admin's change reaches every open
    // dashboard on the next focus or within five minutes. One retry only: the Events
    // page waits on this value, and on failure it falls back to All.
    defaultEventsView: {
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
    // #294 redesign — every open dashboard picks up a change within a minute, background tabs too (they
    // play the sound). One retry; a dashboard that cannot read it uses the defaults.
    criticalSound: {
      staleTime: 60_000,
      refetchInterval: 60_000,
      refetchIntervalInBackground: true,
      retry: 1,
    },
  },
  notifications: {
    unread: {
      staleTime: 30_000,
      refetchInterval: POLL_DASHBOARD_EVENTS,
    },
  },
  // #363 — the site drawings change when a super admin uploads, replaces or removes one, or someone saves pins (each
  // refreshes them). The drawing's link lives 1 hour; a picture that fails to load refetches the drawing once.
  sitePlans: {
    list: {
      staleTime: 5 * 60_000,
    },
    group: {
      staleTime: 10 * 60_000,
    },
  },
  // #309 — G100 Test Console. The camera list also decides whether the page exists: a 404
  // means the console is off, so it is never retried and is read once per session.
  // #403 — the customer settings sheet re-reads the camera's set every 30 s while a change waits for the camera.
  // #411 — the Status and actions sheet re-reads the status and the history as often while an action waits.
  g100Owner: {
    statusWhileWaitingMs: 30_000,
    liveResWhileWaitingMs: 3_000,   // #414 — the Live dialog re-reads the settings this often while a resolution change waits
  },
  g100Console: {
    cameras: {
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: false,
    },
    // Three engineers may share one account: the page stays well under the per-user limits
    // (status every 60 s, log every 10 s, results every 5 s only while one is open).
    info: {
      refetchInterval: 60_000,
    },
    // Results are polled only while one is still waiting to be sent or answered.
    commandsWhileOpenMs: 5_000,
    // #379 — the Settings card: every 5 s while a change waits for the camera, else with the status.
    settingsWhilePendingMs: 5_000,
    settingsMs: 60_000,
    // #381 — the liveAction form follows the camera's live session (opened or ended from the card, another viewer or the camera).
    liveStatusMs: 10_000,
    messages: {
      refetchInterval: 10_000,
    },
    // #310 — the camera reports its audio list with its status, so it is read as often as the status.
    audio: {
      refetchInterval: 60_000,
    },
  },
}

// 409 — pose review. The pose response carries the reviews, so it goes stale after a minute (other reviewers' marks).
export const POSE_STALE_MS = 60_000
// 409 — a fresh video event whose pose row has not landed yet is polled every 10 s for 10 minutes
export const POSE_POLL_MS = 10_000
export const POSE_POLL_WINDOW_MS = 10 * 60_000
